<template>
  <!--
    Marketing im gemeinsamen App-Rahmen. Die Seitenleiste ersetzt die frühere Tab-Leiste,
    die Filterleiste wird in die Toolbar der jeweiligen Seite teleportiert (AppPage #page-toolbar).
  -->
  <AppShell>
    <section v-if="optionsError" class="jr-state jr-state--error">
      <p>{{ optionsError }}</p>
      <button type="button" class="jr-button" @click="retryOptions">Erneut laden</button>
    </section>
    <template v-else>
      <router-view :filter-options="filterOptions" />
      <!-- key: pro Seite neu andocken, weil jede Seite ihr eigenes #page-toolbar rendert. -->
      <Teleport :key="route.name" defer to="#page-toolbar">
        <MarketingFilterBar :options="filterOptions" />
      </Teleport>
    </template>
  </AppShell>
</template>

<script setup>
import { onMounted, provide, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { getApiBaseUrl } from '@/config';
import AppShell from '@/components/shell/AppShell.vue';
import { useAccess } from '@/composables/useAccess';
import MarketingFilterBar from '../components/MarketingFilterBar.vue';
import { provideMarketingFilters } from '../composables/useMarketingFilters';
import '../styles/marketing.css';

const route = useRoute();
const { loaded, loadAccess } = useAccess();
const {
  filters,
  apiParams,
  setFilter,
  applyRouteDefaults,
  clearPageExtras
} = provideMarketingFilters();

const filterOptions = ref({
  products: [],
  marken: [],
  projects: [],
  campaigns: [],
  measures: [],
  channels: [],
  placement_types: [],
  measure_statuses: []
});

// Die Seiten brauchen die Optionen für Breadcrumb und Kontextzeile (Marke · Projekt · Kampagne).
provide('mbFilterOptions', filterOptions);
const optionsError = ref('');
let suppressOptionsReload = false;
let optionsRequestId = 0;
let retriedInvalidProduct = false;

function productIds(products) {
  return (products || []).map((item) => String(item.id));
}

function ensureProduct(products) {
  const ids = productIds(products);
  if (!products?.length) return;
  if (filters.product_id && ids.includes(String(filters.product_id))) return;
  suppressOptionsReload = true;
  setFilter({ product_id: String(products[0].id), project_id: '', campaign_id: '', measure_id: '' });
}

async function loadOptions() {
  const id = ++optionsRequestId;
  optionsError.value = '';
  try {
    const { data } = await axios.get(`${getApiBaseUrl()}/mb/filter-options/`, {
      params: apiParams.value,
      withCredentials: true
    });
    if (id !== optionsRequestId) return;
    filterOptions.value = data;
    retriedInvalidProduct = false;
    ensureProduct(data.products);
  } catch (err) {
    if (id !== optionsRequestId) return;
    if (err.response?.status === 403 && filters.product_id && !retriedInvalidProduct) {
      retriedInvalidProduct = true;
      suppressOptionsReload = true;
      setFilter({ product_id: '', project_id: '', campaign_id: '', measure_id: '' });
      await loadOptions();
      return;
    }
    optionsError.value = err.response?.data?.error || 'Filteroptionen konnten nicht geladen werden.';
  }
}

function retryOptions() {
  loadOptions();
}

onMounted(async () => {
  try {
    if (!loaded.value) await loadAccess();
    applyRouteDefaults();
    await loadOptions();
  } catch (err) {
    optionsError.value = err.response?.data?.error || 'Marketing-Daten konnten nicht geladen werden.';
  }
});

watch(
  () => [
    filters.product_id,
    filters.marke_id,
    filters.project_id,
    filters.campaign_id,
    filters.channel
  ],
  () => {
    if (suppressOptionsReload) {
      suppressOptionsReload = false;
      return;
    }
    loadOptions();
  }
);

watch(
  () => route.name,
  (name, previous) => {
    if (!name || name === previous) return;
    clearPageExtras(name);
    applyRouteDefaults();
  }
);
</script>
